import React, {ReactNode, createContext, useContext, useRef, useState} from 'react';

import {positionInsertWithCustomItemT} from '@/lib/types/position.types';

import {SupabaseInstance} from '@/lib/utils/supabaseClass';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

type wholesalePositionContextT = {
  error: string;
  positions: positionInsertWithCustomItemT[];
  handleAddPosition: () => void;
  handleUpdatePosition: (index: number, data: positionInsertWithCustomItemT) => void;
  handleSubmitPositions: () => void;
  handleDeletePosition: (index: number) => void;
  isSubmitting: boolean;
  acknowledged: boolean;
  setAcknowledged: (acknowledged: boolean) => void;
  setAcknowledging: (acknowledged: boolean) => void;
  acknowledging: boolean;
  totals: {
    subtotal: number;
    marketValue: number;
    potentialProfit: number;
  };
  validatePositions: () => boolean;
  handleDuplicatePosition: (index: number, data: positionInsertWithCustomItemT) => void;
};

const WholesalePositionDataContext = createContext<wholesalePositionContextT | undefined>(undefined);

export const useCreateWholesalePositions = () => {
  const context = useContext(WholesalePositionDataContext);
  if (!context) {
    throw new Error('useWholesalePositionData must be used within a WholesalePositionDataProvider');
  }
  return context;
};

interface WholesalePositionDataProviderProps {
  children: ReactNode;
}

/**
 * Provider for the create wholesale positions page
 * @param children - The children of the provider
 * @returns - The provider
 */
export const CreateWholesalePositionsProvider: React.FC<WholesalePositionDataProviderProps> = ({children}) => {
  const {userAccounts} = useAuth();
  const {toast} = useNotifications();

  // State
  const [error, setError] = useState('');
  const [positions, setPositions] = useState<positionInsertWithCustomItemT[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const totalsRef = useRef({
    subtotal: 0,
    marketValue: 0,
    potentialProfit: 0,
  });

  /**
   * Updates the totals in the ref
   * @param positions - The current positions array
   */
  const updateTotals = (positions: positionInsertWithCustomItemT[]) => {
    const subtotal = positions.reduce((sum, pos) => sum + pos.item.quantity * pos.item.tnc_price, 0);
    const marketValue = positions.reduce(
      (sum, pos) => sum + pos.item.quantity * (pos.item.tnc_price + pos.item.markup),
      0
    );
    const potentialProfit = marketValue - subtotal;

    totalsRef.current = {
      subtotal,
      marketValue,
      potentialProfit,
    };
  };

  /**
   * Handle adding a new position row
   */
  const handleAddPosition = () => {
    if (!userAccounts?.speculator) return;

    const initialPosition: positionInsertWithCustomItemT = {
      item: {
        fk_product_id: null,
        product: null,
        fk_brand_id: null,
        quantity: 0,
        wholesale_price: 0,
        tnc_price: 0,
        factored_price: 0,
        markup: 0,
        min_quantity: 0,
        market: null,
        brand: null,
        position_type: 'wholesale',
      },
      error: false,
    };
    setPositions([...positions, initialPosition]);
  };

  /**
   * This function handles the update of a position - setPosition
   * @param index - The index of the position to update
   * @param data{positionInsertT} - The data to update the position with
   */
  const handleUpdatePosition = (index: number, data: positionInsertWithCustomItemT) => {
    const newPositions = [...positions];
    newPositions[index] = {...newPositions[index], ...data, error: false};
    setPositions(newPositions);
    updateTotals(newPositions);
  };

  /**
   * This function handles the deletion of a position
   * @param index - The index of the position to delete
   */
  const handleDeletePosition = (index: number) => {
    const newPositions = positions.filter((_, i) => i !== index);
    setPositions(newPositions);
    updateTotals(newPositions);
  };

  /**
   * Handles duplicating a position
   * @param index - The index of the position to duplicate
   * @param data - The data to duplicate the position with
   */
  const handleDuplicatePosition = (index: number, data: positionInsertWithCustomItemT) => {
    const newPositions = [...positions];
    newPositions.splice(index + 1, 0, {...newPositions[index], ...data});
    setPositions(newPositions);
  };

  /**
   * This function validates the positions
   * @returns - The new positions array
   */
  const validatePositions = () => {
    if (positions.length === 0) return false;

    // Validate the positions
    const newPositions = positions.map(pos => {
      let errorMessage = '';
      if (!pos.item.position_type) errorMessage = 'Position type is required';
      else if (!pos.item.market) errorMessage = 'Market is required';
      else if (!pos.item.fk_brand_id) errorMessage = 'Brand is required';
      else if (!pos.item.fk_product_id) errorMessage = 'Product is required';
      else if (pos.item.quantity === 0) errorMessage = 'Quantity must be greater than 0';
      else if (isNaN(pos.item.markup) || pos.item.markup < 0)
        errorMessage = 'Markup must be a valid number greater than 0';
      else if (pos.item.markup > pos.item.factored_price) errorMessage = 'Markup must be less than the factored price';
      else if (pos.item.min_quantity && pos.item.quantity % pos.item.min_quantity !== 0)
        errorMessage = `Quantity must be a multiple of ${pos.item.min_quantity}`;

      return {
        ...pos,
        error: !!errorMessage,
        errorMessage,
      };
    });

    setPositions(newPositions);

    // if error then return false else return true
    return newPositions.every(pos => !pos.error);
  };

  /**
   * Handles the submit of the positions
   */
  const handleSubmitPositions = async () => {
    try {
      const {data: supabaseUser} = await SupabaseInstance.supabase.auth.getUser();
      if (!userAccounts.speculator || !supabaseUser || !supabaseUser.user) {
        throw new Error('User not found');
      }
      if (positions.length === 0) {
        throw new Error('No positions to submit');
      }

      setIsSubmitting(true);
      const isValid = validatePositions();

      if (!isValid) {
        console.error('Some positions are invalid. Aborting submission.');
        positions.forEach(pos => {
          console.error(`Position error: ${pos.errorMessage}`);
        });
        setIsSubmitting(false);
        return;
      }

      // Create array of promises for each position
      const positionPromises = positions.map(async position => {
        const {
          item: {fk_product_id, fk_brand_id, quantity, market, rate_id},
        } = position;

        if (!fk_product_id || !fk_brand_id || !market?.market_id || !rate_id) {
          throw new Error('Missing required fields');
        }

        const {data, error} = await SupabaseInstance.supabase.functions.invoke('create-wholesale-order', {
          body: {
            wholesaleOrder: {
              market_id: market.market_id,
              product_id: fk_product_id,
              brand_id: fk_brand_id,
              quantity,
              rate_id,
              markup: position.item.markup,
            },
            user_uid: supabaseUser.user.id,
          },
        });

        if (error) throw error;
        return data;
      });

      // Execute all position creations in parallel
      const results = await Promise.all(positionPromises);

      // Check for any errors in the results
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        console.error('Errors creating positions:', errors);
        toast.error('Error creating some positions');
        return;
      }

      toast.success('Positions created successfully');
    } catch (error) {
      console.error('Error submitting positions:', error);
      toast.error('Error creating positions');
      setError('Error creating position(s)');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Value for the provider
   * @returns - The value for the provider
   */
  const value = {
    error,
    isSubmitting,
    acknowledging,
    setAcknowledging,
    acknowledged,
    setAcknowledged,
    positions,
    totals: totalsRef.current,
    handleAddPosition,
    handleUpdatePosition,
    handleDeletePosition,
    handleSubmitPositions,
    validatePositions,
    handleDuplicatePosition,
  };

  return <WholesalePositionDataContext.Provider value={value}>{children}</WholesalePositionDataContext.Provider>;
};

export default CreateWholesalePositionsProvider;
